/** «Нужен пост»: тип (Любой / Новость / Факт / Миф), пожелание, количество 1–3 → фоновая задача. */
import { useState } from "react";
import { Sparkles, TriangleAlert } from "lucide-react";
import { api, errText } from "../lib/api";
import { countText } from "../lib/format";
import { Modal } from "./ui";

type Kind = "any" | "news" | "fact" | "myth";

const KINDS: { value: Kind; label: string }[] = [
  { value: "any", label: "Любой" },
  { value: "news", label: "Новость" },
  { value: "fact", label: "Факт" },
  { value: "myth", label: "Миф" },
];

const HINT_MAX = 500;

function Segmented<T extends string | number>({
  legend,
  name,
  value,
  options,
  onChange,
}: {
  legend: string;
  name: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="auto-seg">
      <legend className="auto-seg__legend">{legend}</legend>
      <div className="auto-seg__opts">
        {options.map((o) => (
          <label key={String(o.value)} className="auto-seg__opt">
            <input type="radio" name={name} value={String(o.value)} checked={value === o.value} onChange={() => onChange(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function AutoNeedPost({
  open,
  onClose,
  onStarted,
}: {
  open: boolean;
  onClose: () => void;
  onStarted: (jobId: number, count: number) => void;
}) {
  const [kind, setKind] = useState<Kind>("any");
  const [count, setCount] = useState(1);
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (busy) return;
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ jobId: number }>("/api/auto/generate", { kind, count, hint: hint.trim() });
      onStarted(r.jobId, count);
      setHint("");
      onClose();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const verb = count === 1 ? "Подготовить пост" : `Подготовить ${countText(count, "пост", "поста", "постов")}`;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Нужен пост"
      labelledBy="auto-need-title"
      footer={
        <>
          <button className="btn btn--ghost" onClick={close} disabled={busy}>
            Отмена
          </button>
          <button className="btn btn--primary" onClick={() => void submit()} disabled={busy}>
            <Sparkles size={17} aria-hidden="true" />
            {busy ? "Запускаю…" : verb}
          </button>
        </>
      }
    >
      <div className="auto-need">
        <Segmented legend="Что подготовить" name="auto-need-kind" value={kind} options={KINDS} onChange={setKind} />

        <label className="field">
          <span>Пожелание — необязательно</span>
          <textarea
            className="textarea"
            rows={3}
            maxLength={HINT_MAX}
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="Например: про ожоги летом, для родителей школьников"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void submit();
              }
            }}
          />
          {hint.length > HINT_MAX - 80 && <span className="field__hint">Осталось знаков: {HINT_MAX - hint.length}</span>}
        </label>

        <Segmented
          legend="Сколько постов"
          name="auto-need-count"
          value={count}
          options={[1, 2, 3].map((n) => ({ value: n, label: String(n) }))}
          onChange={setCount}
        />

        {error && (
          <div className="error-line" role="alert">
            <TriangleAlert size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
